import { useTheme } from "@/context/ThemeContext";

/**
 * Texture picker — renders 6 preview cards, each showing a swatch of the
 * texture it will apply. Choice is persisted via ThemeContext (localStorage).
 * Sets `data-texture` on <html>. Textures are driven by CSS in index.css.
 */
const OPTIONS = [
  { id: "auto",  label: "Auto",  tagline: "Grain on dark · flat on light" },
  { id: "none",  label: "None",  tagline: "Pure flat surface" },
  { id: "grain", label: "Grain", tagline: "Filmic noise · editorial" },
  { id: "dots",  label: "Dots",  tagline: "Blueprint · engineering" },
  { id: "grid",  label: "Grid",  tagline: "Graph paper · analytical" },
  { id: "paper", label: "Paper", tagline: "Fibre lines · document" },
];

function Swatch({ id }) {
  const base = "h-14 border-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] relative overflow-hidden";
  if (id === "grain" || id === "auto") {
    return (
      <div className={base}>
        <div className="absolute inset-0" style={{
          opacity: 0.4,
          mixBlendMode: "overlay",
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
        }} />
      </div>
    );
  }
  if (id === "dots") {
    return (
      <div className={base}>
        <div className="absolute inset-0" style={{
          opacity: 0.7,
          backgroundImage: "radial-gradient(circle at 1px 1px, var(--peak-ink) 1px, transparent 0)",
          backgroundSize: "10px 10px",
        }} />
      </div>
    );
  }
  if (id === "grid") {
    return (
      <div className={base}>
        <div className="absolute inset-0" style={{
          opacity: 0.55,
          backgroundImage:
            "linear-gradient(to right, var(--peak-ink) 1px, transparent 1px), linear-gradient(to bottom, var(--peak-ink) 1px, transparent 1px)",
          backgroundSize: "14px 14px",
        }} />
      </div>
    );
  }
  if (id === "paper") {
    return (
      <div className={base}>
        <div className="absolute inset-0" style={{
          opacity: 0.6,
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent 0, transparent 2px, var(--peak-ink) 2px, var(--peak-ink) 3px)",
        }} />
      </div>
    );
  }
  return <div className={base} />; // none
}

export default function TexturePicker() {
  const { texture, setTexture } = useTheme();
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="texture-picker">
      {OPTIONS.map((o) => {
        const active = texture === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => setTexture(o.id)}
            data-testid={`texture-option-${o.id}`}
            data-active={active ? "1" : "0"}
            className={`text-left border-2 p-3 transition-all hover:-translate-y-0.5 ${
              active
                ? "border-[var(--peak-accent)] shadow-[4px_4px_0_0_var(--peak-shadow)] peak-glow"
                : "border-[var(--peak-ink)] hover:shadow-[4px_4px_0_0_var(--peak-shadow)]"
            } bg-[var(--peak-surface)]`}
          >
            <Swatch id={o.id} />
            <div className="mt-2 font-heading font-black tracking-tight text-sm leading-tight text-[var(--peak-ink)]">
              {o.label}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-0.5">
              {o.tagline}
            </div>
          </button>
        );
      })}
    </div>
  );
}
